import {
  BracketStage,
  BracketMatch,
  BracketReadResponse,
  BracketRound,
  BracketSlot,
  TeamSummary
} from '../core/tournaments-api.types';

export type BracketStageKey = 'winner' | 'loser' | 'final' | 'other';

export interface BracketTeamRef extends TeamSummary {
  seed?: number | null;
}

export interface BracketSlotView {
  slot_number: number;
  team: BracketTeamRef | null;
  source_label: string | null;
  is_bye: boolean;
  source_match_id: number | null;
  source_outcome: 'winner' | 'loser' | null;
}

export interface BracketMatchView {
  id: number;
  match_number: number;
  display_match_number: number;
  stage: BracketStageKey;
  position: number;
  status: string;
  best_of: number | null;
  winner_to_match_id: number | null;
  loser_to_match_id: number | null;
  reset_match_id: number | null;
  winner_team: BracketTeamRef | null;
  slots: BracketSlotView[];
}

export interface BracketRoundView {
  id: number;
  round_number: number;
  position: number;
  title: string;
  stage: BracketStageKey;
  matches: BracketMatchView[];
}

export interface BracketStageView {
  key: BracketStageKey;
  rounds: number[];
}

export interface BracketBoardView {
  tournament: BracketReadResponse['tournament'];
  bracket: BracketReadResponse['bracket'];
  schemaVersion: number;
  stages: BracketStageView[];
  rounds: BracketRoundView[];
}

export function mapBracketReadResponseToBoardView(response: BracketReadResponse): BracketBoardView {
  const highestRoundNumber = response.bracket.rounds.reduce((max, round) => {
    return Math.max(max, round.round_number);
  }, 0);

  const rounds = response.bracket.rounds
    .map((round) => mapRound(round, inferRoundStage(round, highestRoundNumber)))
    .sort((a, b) => {
      const stageDiff = stagePriority(a.stage) - stagePriority(b.stage);
      if (stageDiff !== 0) return stageDiff;
      if (a.position !== b.position) return a.position - b.position;
      return a.round_number - b.round_number;
    })
    .map((round) => ({ ...round, matches: round.matches.map((match) => ({ ...match })) }));

  if (response.bracket.bracket_kind === 'simple_single') {
    let displayMatchNumber = 1;
    for (const round of rounds) {
      for (const match of round.matches) {
        match.display_match_number = displayMatchNumber;
        displayMatchNumber += 1;
      }
    }
  }

  return {
    tournament: response.tournament,
    bracket: response.bracket,
    schemaVersion: response.bracket.schema_version ?? 1,
    stages: mapStages(response.bracket.stages),
    rounds
  };
}

function mapRound(round: BracketRound, stage: BracketStageKey): BracketRoundView {
  const orderedMatches = [...round.matches].sort((a, b) => fallbackMatchPosition(a) - fallbackMatchPosition(b));

  return {
    id: round.id,
    round_number: round.round_number,
    position: fallbackRoundPosition(round),
    title: round.title,
    stage,
    matches: orderedMatches.map((match) => mapMatch(match, stage))
  };
}

function mapMatch(match: BracketMatch, stage: BracketStageKey): BracketMatchView {
  const resolvedStage = normalizeStage(match.stage) ?? stage;

  return {
    id: match.id,
    match_number: match.match_number,
    display_match_number: match.match_number,
    stage: resolvedStage,
    position: fallbackMatchPosition(match),
    status: match.status ?? 'pending',
    best_of: match.best_of ?? null,
    winner_to_match_id: match.winner_to_match_id ?? null,
    loser_to_match_id: match.loser_to_match_id ?? null,
    reset_match_id: match.reset_match_id ?? null,
    winner_team: match.winner_team ? mapTeam(match.winner_team) : null,
    slots: match.slots.map((slot) => mapSlot(slot))
  };
}

function mapSlot(slot: BracketSlot): BracketSlotView {
  return {
    slot_number: slot.slot_number,
    team: slot.team ? mapTeam(slot.team) : null,
    source_label: slot.source_label,
    is_bye: slot.is_bye ?? (!slot.team && (slot.source_label?.toLowerCase().includes('bye') ?? false)),
    source_match_id: slot.source_match_id ?? null,
    source_outcome: normalizeOutcome(slot.source_outcome)
  };
}

function mapTeam(team: TeamSummary): BracketTeamRef {
  return {
    ...team
  };
}

function inferRoundStage(round: BracketRound, highestRoundNumber: number): BracketStageKey {
  const normalized = normalizeStage(round.stage);
  if (normalized) return normalized;

  const title = (round.title ?? '').toLowerCase();
  if (title.includes('loser')) return 'loser';
  if (title.includes('grand final') || title.includes('final stage')) return 'final';
  if (round.round_number === highestRoundNumber) return 'final';
  return 'winner';
}

function fallbackRoundPosition(round: BracketRound): number {
  return round.position ?? round.round_number;
}

function fallbackMatchPosition(match: BracketMatch): number {
  return match.position ?? match.match_number;
}

function normalizeStage(value: string | undefined): BracketStageKey | null {
  if (value === 'winner' || value === 'loser' || value === 'final') return value;
  return null;
}

function normalizeOutcome(value: 'winner' | 'loser' | null | undefined): 'winner' | 'loser' | null {
  if (value === 'winner' || value === 'loser') return value;
  return null;
}

function mapStages(stages: BracketStage[] | undefined): BracketStageView[] {
  if (!Array.isArray(stages) || stages.length === 0) {
    return [];
  }

  return stages
    .map((stage) => ({
      key: normalizeStage(stage.key) ?? 'other',
      rounds: Array.isArray(stage.rounds) ? stage.rounds : []
    }))
    .sort((a, b) => stagePriority(a.key) - stagePriority(b.key));
}

export function stagePriority(stage: BracketStageKey): number {
  if (stage === 'winner') return 1;
  if (stage === 'loser') return 2;
  if (stage === 'final') return 3;
  return 4;
}
